import React, { useEffect, useState, useCallback } from "react";
import ActionBar from "../../common/ActionBar";
import { Formik, Form } from "formik";
import { CustomFileUpload, CustomInputField } from "../../common/FormInputs";
import useLoader from "../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import * as XLSX from "xlsx";
import styled from "styled-components";
import COLORS from "../../../assets/Colors";
import CustomSelect from "../../common/CustomSelect/CustomSelect";
import {
  PrimaryCTAButton,
  PrimaryOutlinedCTAButton,
} from "../../common/Buttons";
import { Grid, RadioGroup, FormControlLabel, Radio } from "@material-ui/core";
import PartyService from "../../../services/PartyService";
import CaseService from "../../../services/CaseService";
import PreCaseLoad from "../EsignerTable/component/PreCaseLoad";
import AsyncCreatableSelect from "react-select/async-creatable";

const VirtualSelect = React.memo((props) => (
  <AsyncCreatableSelect
    {...props}
    cacheOptions
    defaultOptions
    isMulti
    isClearable
    isSearchable
    closeMenuOnSelect={false}
    hideSelectedOptions={false}
    menuPortalTarget={document.body}
    menuPosition="fixed"
    maxMenuHeight={280}
    styles={{
      control: (base, state) => ({
        ...base,
        height: 42,
        minHeight: 42,
        maxHeight: 42,
        borderRadius: 8,
        borderColor: state.isFocused ? COLORS.BTN_GREEN : "#d0d0d0",
        boxShadow: state.isFocused
          ? `0 0 0 2px ${COLORS.BTN_GREEN}25`
          : "0 1px 2px rgba(0,0,0,0.05)",
        backgroundColor: "#fff",
        transition: "all 0.2s ease",
        cursor: "pointer",
        padding: "0 8px",
        "&:hover": { borderColor: COLORS.BTN_GREEN },
      }),
      valueContainer: (base) => ({
        ...base,
        height: 38,
        maxHeight: 38,
        padding: "0 6px",
        overflow: "hidden",
      }),
      input: (base) => ({
        ...base,
        margin: 0,
        padding: "2px 0",
        color: "#293461",
        fontFamily: "openSans-SemiBold, sans-serif",
      }),
      menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      }),
      menu: (base) => ({
        ...base,
        borderRadius: 8,
        overflow: "hidden",
      }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
          ? COLORS.BORDER_GREY
          : state.isFocused
            ? `${COLORS.BTN_GREEN}12`
            : "white",
        color: "#293461",
        borderRadius: 0,
        fontFamily: "openSans-SemiBold, sans-serif",
        margin: 0,
        cursor: "pointer",
        transition: "all 0.1s ease",
        padding: "8px 12px",
        fontSize: "13px",
      }),
      multiValue: (base) => ({
        ...base,
        backgroundColor: `${COLORS.COLOR_DARK}15`,
        borderRadius: 5,
        border: `1px solid ${COLORS.COLOR_DARK}40`,
        padding: "1px 2px",
        fontSize: "12px",
        margin: "1px 2px",
      }),
      multiValueLabel: (base) => ({
        ...base,
        color: COLORS.COLOR_DARK,
        fontWeight: 600,
        padding: "0 4px",
        fontSize: "12px",
      }),
      multiValueRemove: (base) => ({
        ...base,
        color: COLORS.LOGOUT_RED,
        padding: "0 3px",
        ":hover": {
          backgroundColor: COLORS.LOGOUT_RED,
          color: "white",
          borderRadius: "3px",
        },
      }),
      placeholder: (base) => ({
        ...base,
        color: "#aaa",
        fontSize: "13px",
      }),
      clearIndicator: (base) => ({
        ...base,
        padding: "4px",
      }),
    }}
    components={{
      DropdownIndicator: () => null,
      IndicatorSeparator: () => null,
    }}
  />
));

const FieldMapping = () => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const [partyCodes, setPartyCodes] = useState([]);
  const [excelHeaders, setExcelHeaders] = useState([]);
  const [fileName, setFileName] = useState("");
  const [preModal, setPreModal] = useState({ state: false });
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [number, setNumber] = useState(7);

  const FIELD_CONFIG = [
    "Description",
    "Contract No",
    "Total Claim Value",
    "Company Law",
    "Claimant's field of business",
    "Date of Agreement",
    "Purpose of Agreement",
    "Date of Arbitration Notice",
    "Date of intimation",
    "Date of Response",
    "Name of Agreement",
    "Lot ID",
    "Content",
    "Place of Award",
    ...Array.from({ length: number }, (_, i) => [
      `RespondentKind_${i + 1}`,
      `RespondentName_${i + 1}`,
      `RespondentEmail_${i + 1}`,
      `RespondentMobile_${i + 1}`,
      `RespondentPanNumber_${i + 1}`,
      `RespondentAddress_${i + 1}`,
    ]).flat(),
    "Statement of Claim Prayer 1",
    "Statement of Claim Prayer 2",
    "Statement of Claim Prayer 3",
    "Averment,Submission 1",
    "Submission 2",
    "Submission 3",
    "Submission 4",
    "Submission 5",
    "Submission 6",
    "Any other averment",
    "Statement of Defence Prayer 1",
    "Statement of Defence Prayer 2",
    "Statement of Defence Prayer 3",
    "Individual/Organisation Description",
    "Reference of Clause",
    "Date of Agreement – Arbitration Notice",
    "Prayer 1 – Arbitration Notice",
    "Prayer 2 – Arbitration Notice",
    "Interim Relief sought",
    "Interim relief 1",
    "Interim relief 2",
    "Interim relief 3",
    "Date of Affidavit",
    "Date of Award",
  ];

  const actions = [
    {
      text: "Cases pre upload",
      props: { onClick: () => setPreModal({ state: true }) },
    },
  ];

  useEffect(() => {
    let mounted = true;
    async function loadPartyCodes() {
      try {
        setLoader({ state: true, message: "Loading party codes..." });
        const res = await PartyService.getPartyCodes();
        if (!mounted) return;
        setPartyCodes(
          res.map((p) => ({ label: p.customerCode, value: p.customerCode })),
        );
      } catch (err) {
        enqueueSnackbar("Failed to load party codes", { variant: "error" });
      } finally {
        setLoader({ state: false });
      }
    }
    loadPartyCodes();
    return () => (mounted = false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadOptions = useCallback(
    (inputValue) => {
      return new Promise((resolve) => {
        const filtered = excelHeaders
          .filter((h) => h.toLowerCase().includes(inputValue.toLowerCase()))
          .map((h) => ({ label: h, value: h }));
        resolve(filtered);
      });
    },
    [excelHeaders],
  );

  const handleFileUpload = async (file, setFieldValue) => {
    if (!file) return;
    setLoader({ state: true, message: "Reading Excel file..." });

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const headers =
          XLSX.utils.sheet_to_json(firstSheet, { header: 1 })[0] || [];

        setExcelHeaders(headers);
        setFileName(file.name);
        setFieldValue("fileName", file.name);
        setExpandedGroup(0);
        enqueueSnackbar(`${headers.length} columns detected!`, {
          variant: "success",
        });
      } catch (err) {
        enqueueSnackbar("Invalid or empty Excel file", { variant: "error" });
      } finally {
        setLoader({ state: false });
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    const mapping = {};
    FIELD_CONFIG.forEach((field) => {
      const selected = values[field] || [];
      if (Array.isArray(selected) && selected.length > 0) {
        mapping[field] = selected.map((item) => item.value || item);
      }
    });

    if (number < 1) {
      enqueueSnackbar(
        "Number of respondents cannot be empty. Please provide a valu",
        {
          variant: "error",
        },
      );
      return;
    }

    if (!values.partyCode) {
      enqueueSnackbar("Please select a party code", { variant: "warning" });
      return;
    }

    try {
      setLoader({ state: true, message: "Saving field mapping..." });
      if (values.mappingType === "createMapping") {
        await CaseService.createExcelMapping({
          partyCode: values.partyCode,
          excelMapping: mapping,
        });
        enqueueSnackbar("Field mapping created successfully!", {
          variant: "success",
        });
      } else {
        await CaseService.updateExcelMapping(values.partyCode, {
          excelMapping: mapping,
        });
        enqueueSnackbar("Field mapping updated successfully!", {
          variant: "success",
        });
      }
      setExcelHeaders([]);
      setFileName("");
      setExpandedGroup(null);
      setSubmitting(false);
    } catch (err) {
      console.error(err);
      enqueueSnackbar(err?.message || "Failed to save mapping", {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
    }
  };

  const fetchExistingMapping = async (partyCode, setFieldValue) => {
    if (!partyCode) {
      enqueueSnackbar("Select a party code first", { variant: "info" });
      return;
    }
    try {
      setLoader({ state: true, message: "Fetching saved mapping..." });
      const res = await CaseService.getExcelMapping(partyCode);
      const saved =
        res && res[0] && res[0].excelMapping ? res[0].excelMapping : {};

      FIELD_CONFIG.forEach((field) => {
        const cols = saved[field] || [];
        setFieldValue(
          field,
          cols.map((c) => ({ label: c, value: c })),
        );
      });

      // keep unique headers
      const flattened = Array.from(new Set(Object.values(saved).flat()));
      setExcelHeaders(flattened.filter(Boolean));
      enqueueSnackbar("Existing mapping loaded", { variant: "success" });
    } catch (err) {
      console.error(err);
      enqueueSnackbar("No saved mapping found for this party", {
        variant: "info",
      });
    } finally {
      setLoader({ state: false });
    }
  };

  return (
    <>
      <ActionBar breadcrumbs={["Field Mapping"]} actions={actions} />

      <PageWrapper>
        <Formik
          initialValues={{
            mappingType: "createMapping",
            partyCode: "",
            fileName: "",
          }}
          enableReinitialize
          onSubmit={handleSubmit}
        >
          {/* {({ values, setFieldValue, resetForm }) => ( */}

          {({ setFieldValue, resetForm, values }) => {
            const radioOptions = (() => {
              if (!values.partyCode) {
                return ["createMapping", "fetchMapping"]; // show both
              }

              if (values.mappingType === "createMapping") {
                return ["createMapping"];
              }

              if (values.mappingType === "fetchMapping") {
                return ["fetchMapping"];
              }

              return [];
            })();
            return (
              <StyledForm>
                <TopRow>
                  <StepCard style={{ flex: 1 }}>
                    <StepCardHeader>
                      Step 1: Select Party & No of respondent
                    </StepCardHeader>
                    <CardContent>
                      <CustomSelect
                        value={values.partyCode}
                        onChange={async (e) => {
                          const party = e.target.value;
                          setFieldValue("partyCode", party);

                          setExcelHeaders([]);
                          setFileName("");
                          setExpandedGroup(null);

                          if (!party) return; // no API call if empty

                          setLoader({
                            state: true,
                            message: "Checking existing mapping...",
                          });
                          try {
                            const res =
                              await CaseService.getExcelMapping(party);

                            if (res && res.length > 0 && res[0]?.excelMapping) {
                              setFieldValue("mappingType", "fetchMapping");
                              const saved = res[0].excelMapping || {};
                              FIELD_CONFIG.forEach((field) => {
                                const cols = saved[field] || [];
                                setFieldValue(
                                  field,
                                  cols.map((c) => ({ label: c, value: c })),
                                );
                              });
                              const flattened = Array.from(
                                new Set(Object.values(saved).flat()),
                              );
                              setExcelHeaders(flattened.filter(Boolean));
                            } else {
                              setFieldValue("mappingType", "createMapping");
                              setExcelHeaders([]);
                            }
                          } catch (err) {
                            setFieldValue("mappingType", "createMapping");
                            setExcelHeaders([]);
                          } finally {
                            setLoader({ state: false });
                          }
                        }}
                        menuItemValues={partyCodes}
                        placeholder="Select party code"
                      />
                      <CustomInputField
                        variant="outlined"
                        type="number"
                        value={number}
                        onChange={(e) => setNumber(e.target.value)}
                        menuItemValues={partyCodes}
                        placeholder="Enter the number of respondents"
                        style={{ width: "100%", maxWidth: 420, marginTop: 10 }}
                      />
                      {number < 1 && (
                        <ErrorText>
                          {"Number of respondents is required"}
                        </ErrorText>
                      )}
                    </CardContent>
                  </StepCard>

                  <StepCard style={{ flex: 1 }}>
                    <StepCardHeader>Step 2: Choose Mode</StepCardHeader>
                    <CardContent>
                      <RadioGroup
                        row
                        value={values.mappingType}
                        onChange={(e) =>
                          setFieldValue("mappingType", e.target.value)
                        }
                      >
                        {radioOptions.includes("createMapping") && (
                          <StyledRadio
                            value="createMapping"
                            label="Create Excel"
                          />
                        )}

                        {radioOptions.includes("fetchMapping") && (
                          <StyledRadio
                            value="fetchMapping"
                            label="Load Existing"
                          />
                        )}
                      </RadioGroup>
                    </CardContent>
                  </StepCard>

                  <StepCard style={{ flex: 1 }}>
                    <StepCardHeader>Step 3: Upload Excel</StepCardHeader>
                    <CardContent>
                      <CustomFileUpload
                        accept=".xlsx"
                        file={{ name: fileName }}
                        setFile={(file) =>
                          handleFileUpload(file, setFieldValue)
                        }
                        style={{ width: "100%", maxWidth: 420 }}
                      />
                      {fileName && (
                        <SuccessMsg>
                          ✓ {fileName} ({excelHeaders.length} columns)
                        </SuccessMsg>
                      )}
                    </CardContent>
                  </StepCard>
                </TopRow>

                {excelHeaders.length > 0 && (
                  <MappingWrapper>
                    <GroupCardWrapper>
                      <GroupCardHeader
                        isExpanded={expandedGroup === 0}
                        onClick={() =>
                          setExpandedGroup(expandedGroup === 0 ? null : 0)
                        }
                      >
                        <GroupHeaderContent>
                          <GroupTitle>All Fields</GroupTitle>
                          <FieldBadge>{FIELD_CONFIG.length}</FieldBadge>
                        </GroupHeaderContent>
                        <ChevronIcon isExpanded={expandedGroup === 0}>
                          ▼
                        </ChevronIcon>
                      </GroupCardHeader>

                      {expandedGroup === 0 && (
                        <GroupFieldsContainer>
                          <Grid container spacing={2}>
                            {FIELD_CONFIG.map((field) => (
                              <Grid item xs={12} sm={6} lg={4} key={field}>
                                <FieldItemWrapper>
                                  <FieldLabelSmall>
                                    {field.replace(/_/g, " ")}
                                  </FieldLabelSmall>

                                  <VirtualSelect
                                    loadOptions={loadOptions}
                                    placeholder="Search..."
                                    value={values[field] || []}
                                    onChange={(selected) =>
                                      setFieldValue(field, selected || [])
                                    }
                                    isDisabled={excelHeaders.length === 0}
                                  />
                                </FieldItemWrapper>
                              </Grid>
                            ))}
                          </Grid>
                        </GroupFieldsContainer>
                      )}
                    </GroupCardWrapper>
                  </MappingWrapper>
                )}

                <ButtonSection>
                  <PrimaryOutlinedCTAButton
                    style={{ width: "180px" }}
                    onClick={() => {
                      resetForm();
                      setExcelHeaders([]);
                      setFileName("");
                      setExpandedGroup(null);
                    }}
                  >
                    Clear All
                  </PrimaryOutlinedCTAButton>
                  {values.mappingType === "createMapping" ? (
                    <PrimaryCTAButton
                      style={{ width: "180px" }}
                      type="submit"
                      disabled={!values.partyCode}
                    >
                      Create Excel
                    </PrimaryCTAButton>
                  ) : (
                    <PrimaryCTAButton
                      style={{ width: "180px" }}
                      type="submit"
                      disabled={!values.partyCode}
                      onClick={() =>
                        fetchExistingMapping(values.partyCode, setFieldValue)
                      }
                    >
                      Load Existing
                    </PrimaryCTAButton>
                  )}
                </ButtonSection>
              </StyledForm>
            );
          }}
        </Formik>
      </PageWrapper>

      {preModal.state && (
        <PreCaseLoad preModal={preModal} setPreModal={setPreModal} />
      )}
    </>
  );
};

export default FieldMapping;

export const PageWrapper = styled.div`
  padding: 0;
  min-height: calc(100vh - 140px);
  overflow-y: auto;
  background: #f8fafc;
  padding-bottom: 40px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #e2e8f0;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;

    &:hover {
      background: #94a3b8;
    }
  }
`;

export const StyledForm = styled(Form)`
  padding: 24px;
  max-width: 1600px;
  margin: 0 auto;
`;

export const TopRow = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

export const StepCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  margin-bottom: 20px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  min-width: 280px;
`;

export const StepCardHeader = styled.div`
  padding: 14px 20px;
  background: linear-gradient(90deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid #e2e8f0;
  font-weight: 700;
  color: #293461;
  font-size: 13px;
  font-family: openSans-SemiBold, sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const CardContent = styled.div`
  padding: 18px 20px;
`;

export const SuccessMsg = styled.div`
  color: ${COLORS.BTN_GREEN};
  margin-top: 10px;
  font-weight: 600;
  font-size: 13px;
  padding: 10px 12px;
  background: ${COLORS.BTN_GREEN}12;
  border-radius: 6px;
  border-left: 3px solid ${COLORS.BTN_GREEN};
`;

const MappingWrapper = styled.div`
  margin-top: 8px;
  margin-bottom: 24px;
`;

const GroupCardWrapper = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  margin-bottom: 12px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease;

  &:hover {
    border-color: ${COLORS.BTN_GREEN}50;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  }
`;

const GroupCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 18px;
  background: ${(props) =>
    props.isExpanded
      ? `${COLORS.BTN_GREEN}08`
      : "linear-gradient(90deg, #fafbfc 0%, #f3f6f9 100%)"};
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) =>
      props.isExpanded
        ? `${COLORS.BTN_GREEN}12`
        : "linear-gradient(90deg, #f3f6f9 0%, #eef2f7 100%)"};
  }
`;

const GroupHeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const GroupTitle = styled.span`
  font-weight: 700;
  color: #1e293b;
  font-size: 14px;
  color: #293461;
  font-family: openSans-SemiBold, sans-serif;
`;

const FieldBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  background: ${COLORS.BTN_GREEN}20;
  color: ${COLORS.BTN_GREEN};
  border-radius: 5px;
  font-size: 11px;
  font-weight: 700;
  margin-left: 6px;
`;

const ChevronIcon = styled.div`
  font-size: 12px;
  color: #64748b;
  transition: transform 0.25s ease;
  transform: ${(props) => (props.isExpanded ? "rotate(180deg)" : "rotate(0)")};
`;

const GroupFieldsContainer = styled.div`
  padding: 18px;
  background: #fafbfc;
  border-top: 1px solid #e2e8f0;
`;

const FieldItemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
`;

const FieldLabelSmall = styled.label`
  font-size: 12px;
  color: #8294a5;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-family: openSans-SemiBold, sans-serif;
`;

export const ButtonSection = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin: 32px 0 0 0;
  padding-top: 20px;
  border-top: 1px solid #e2e8f0;
`;

export const StyledRadio = ({ label, ...props }) => (
  <FormControlLabel
    control={<Radio style={{ color: "#00838c" }} />}
    {...props}
    label={
      <span
        style={{
          fontSize: "16px",
          fontFamily: "openSans-SemiBold, sans-serif",
          color: "#293461",
        }}
      >
        {label}
      </span>
    }
    style={{
      marginRight: 28,
      marginBottom: 0,
      fontSize: "14px",
      color: "#293461",
      fontFamily: "openSans-SemiBold, sans-serif",
    }}
  />
);

const ErrorText = styled.span`
  font-size: 10px;
  color: #f44336;
  font-family: openSans-SemiBold, sans-serif;
  bottom: -16px;
  left: 3px;
`;
